<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import dayjs from 'dayjs';
import ActionButton from '@/components/ui/ActionButton.vue';
import FilterChip from '@/components/ui/FilterChip.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import type { TimeMode } from '@/stores/journey';
import { useAppStore } from '@/stores/app';

/**
 * « Partir maintenant », « Partir à » or « Arriver à », with a date bounded by the
 * GTFS loaded: the network has no timetable outside of it.
 */
const props = defineProps<{
  mode: TimeMode;
  /** Epoch seconds. */
  time: number;
  validity?: { start: string; end: string };
}>();

const emit = defineEmits<{ apply: [mode: TimeMode, time: number]; close: [] }>();

const appStore = useAppStore();

const MODES: TimeMode[] = ['now', 'depart', 'arrive'];

const draftMode = ref<TimeMode>(props.mode);
const initial = dayjs(props.mode === 'now' ? Date.now() : props.time * 1000);
const draftDate = ref<string>(initial.format('YYYY-MM-DD'));
const draftClock = ref<string>(initial.format('HH:mm'));

const toInputDate = (dateGtfs: string) => dayjs(dateGtfs, 'YYYYMMDD').format('YYYY-MM-DD');
const minDate = computed<string | undefined>(() => props.validity && toInputDate(props.validity.start));
const maxDate = computed<string | undefined>(() => props.validity && toInputDate(props.validity.end));

const picked = computed(() => dayjs(`${draftDate.value}T${draftClock.value}`));
/** A cleared date or time must not turn into a « Partir à » frozen at the current time. */
const canApply = computed<boolean>(() => draftMode.value === 'now' || picked.value.isValid());

function apply() {
  if (!canApply.value) return;
  emit(
    'apply',
    draftMode.value,
    draftMode.value === 'now' ? Math.floor(Date.now() / 1000) : picked.value.unix(),
  );
}

let unregisterBackHandler: (() => void) | undefined;
onMounted(() => {
  unregisterBackHandler = appStore.registerBackHandler(() => {
    emit('close');
    return true;
  });
});
onBeforeUnmount(() => unregisterBackHandler?.());
</script>

<template>
  <!-- Out of the page's stacking context, over the tab bar. -->
  <Teleport to="body">
    <div class="time-selector" @click.self="emit('close')">
      <div class="time-selector__sheet" role="dialog" :aria-label="$t('itinerary.time.title')">
        <div class="time-selector__header">
          <h2 class="time-selector__title">{{ $t('itinerary.time.title') }}</h2>
          <RoundedButton :aria-label="$t('common.back')" icon="x" type="ghost" @click="emit('close')" />
        </div>

        <div class="time-selector__modes">
          <FilterChip
            v-for="option in MODES"
            :key="option"
            :active="draftMode === option"
            :label="$t(`itinerary.time.${option}`)"
            @click="draftMode = option"
          />
        </div>

        <div v-if="draftMode !== 'now'" class="time-selector__inputs">
          <label class="time-selector__input">
            <span>{{ $t('itinerary.time.date') }}</span>
            <input v-model="draftDate" type="date" :min="minDate" :max="maxDate" />
          </label>
          <label class="time-selector__input">
            <span>{{ $t('itinerary.time.hour') }}</span>
            <input v-model="draftClock" type="time" />
          </label>
        </div>

        <ActionButton
          class="time-selector__apply"
          :disabled="!canApply"
          :label="$t('itinerary.time.apply')"
          size="large"
          type="primary"
          @click="apply"
        />
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
.time-selector {
  align-items: flex-end;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  inset: 0;
  position: fixed;
  z-index: 5;

  &__sheet {
    background-color: $color-white;
    border-radius: $radius-2xl $radius-2xl 0 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    padding: $spacing-4 $spacing-4 calc(#{$spacing-4} + env(safe-area-inset-bottom));
    width: 100%;
  }

  &__header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  &__title {
    color: $color-neutral-highest;
    font: $heading-h3;
    letter-spacing: $heading-h3-spacing;
    margin: 0;
  }

  &__modes {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
  }

  // The native date and time fields keep a minimum width: below it, they go one under the other.
  &__inputs {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-3;
  }

  &__input {
    color: $color-neutral-higher;
    display: flex;
    flex: 1 1 10rem;
    flex-direction: column;
    min-width: 0;
    font: $label-small;
    gap: $spacing-1;
    letter-spacing: $label-small-spacing;

    input {
      background-color: $color-white;
      border: 1px solid $color-border-medium;
      border-radius: $radius-card;
      color: $color-neutral-highest;
      box-sizing: border-box;
      font: $label-medium;
      min-width: 0;
      padding: $spacing-3;
      width: 100%;
    }
  }

  &__apply {
    width: 100%;
  }
}
</style>
